<template>
  <div class="m-auto md:max-w-[60%] relative w-full p-8" :class="modalStyle">
    <button
      class="absolute max-md:hidden right-4 md:-right-2 -top-2 translate-x-full text-white -translate-y-full"
      @click="$emit('close')"
    >
      <IconX class="size-4" />
    </button>
    <div>
      <UIHeading :title="title" tag="2" class="mb-8" />

      <YandexMap
        :settings="{
          location: mapLocation,
          theme,
        }"
        real-settings-location
        class="rounded border border-neutral-200 dark:border-gray-700 overflow-hidden mt-2"
        :height="'500px'"
      >
        <YandexMapDefaultSchemeLayer />
        <YandexMapDefaultFeaturesLayer />

        <YandexMapFeature
          v-for="(item, i) in addressesFeatures"
          :key="item.id || i"
          :settings="item"
        />
        <YandexMapDefaultMarker
          v-for="marker in markerEntries"
          :key="marker.key"
          :settings="{
            id: marker.key,
            properties: marker,
            coordinates: marker.coordinates,
            color: marker.color,
            hideOutsideViewport: false,
            zIndex: 1800,
          }"
        />
      </YandexMap>
    </div>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue'
import {
  YandexMap,
  YandexMapDefaultFeaturesLayer,
  YandexMapDefaultMarker,
  YandexMapDefaultSchemeLayer,
  YandexMapFeature,
} from 'vue-yandex-maps'

import { IconX } from '@tabler/icons-vue'

import UIHeading from '@/components/ui/UIHeading.vue'
import { modalStyle } from '@/utils/ui.js'

const theme = inject('theme')
const center = [33, 68.9545]
const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  zoom: {
    type: Number,
    default: 8,
  },
  addresses: {
    type: Array,
    default: () => [],
  },
})

function toFiniteNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

function getAddressCoordinates(address) {
  const longitude = toFiniteNumber(address?.longitude)
  const latitude = toFiniteNumber(address?.latitude)

  if (longitude !== null && latitude !== null) {
    return [longitude, latitude]
  }

  const coordinates = address?.coordinates || address?.geometry?.coordinates

  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return []
  }

  const normalizedCoordinates = coordinates.slice(0, 2).map(toFiniteNumber)

  return normalizedCoordinates.every((value) => value !== null)
    ? normalizedCoordinates
    : []
}

const markerEntries = computed(() =>
  props.addresses
    .map((address, i) => {
      const coordinates = getAddressCoordinates(address)
      const key =
        address?.fias_id ||
        address?.id ||
        `${i}-${coordinates.join(',')}`

      return {
        key: String(key),
        color: address?.color || '#4156cf',
        coordinates,
      }
    })
    .filter((item) => item.coordinates.length),
)

const mapLocation = computed(() => {
  const firstMarker = markerEntries.value[0]

  if (firstMarker) {
    return {
      center: firstMarker.coordinates,
      zoom: 15,
    }
  }

  return {
    center: center,
    zoom: props.zoom,
  }
})

const addressesFeatures = computed(() => {
  return (
    props.addresses
      .flatMap((item) => item?.meta?.features || item?.features || [])
      .filter((item) => item?.geometry?.type !== 'Point') || []
  )
})
</script>
